import type { ISODateTimeString, UUID } from './shared';
import type { UserDetail } from './interaction';

// ── Status enum ─────────────────────────────────────────────────────────────
// Matches backend `WeMetStatus` values in `app/db/models/we_met_connection.py`.

export const WeMetConnectionStatus = {
  NOT_CONFIRMED: 'not_confirmed',
  CONFIRMED: 'confirmed',
  DELETED: 'deleted',
} as const;

export type WeMetConnectionStatus =
  (typeof WeMetConnectionStatus)[keyof typeof WeMetConnectionStatus];

// ── Place (where two users met) ───────────────────────────────────────────────

/**
 * Exactly one place where two users met. Discriminated union so the "both ids"
 * and "neither id" states are unconstructable at compile time — the backend
 * `WeMetConnectionCreate` schema has no exactly-one-of validator, so this type
 * (plus {@link toWeMetConnectionCreate}) is the only thing keeping event/space
 * mutually exclusive on the wire.
 */
export type WeMetPlace = { kind: 'event'; id: UUID } | { kind: 'space'; id: UUID };

// ── Request body ────────────────────────────────────────────────────────────

/**
 * Body for `POST /api/v1/interactions/we-met/v2`.
 *
 * Construct it ONLY via {@link toWeMetConnectionCreate} — the raw shape allows
 * both/neither ids (mirroring the loose backend schema), which the mapper
 * prevents.
 */
export interface WeMetConnectionCreate {
  target_user_id: UUID;
  event_id?: UUID | null;
  space_id?: UUID | null;
}

/** Build a v2 create body from a single {@link WeMetPlace}. */
export function toWeMetConnectionCreate(
  targetUserId: UUID,
  place: WeMetPlace,
): WeMetConnectionCreate {
  return {
    target_user_id: targetUserId,
    event_id: place.kind === 'event' ? place.id : null,
    space_id: place.kind === 'space' ? place.id : null,
  };
}

// ── Response shape ──────────────────────────────────────────────────────────

/** Single row returned by all v2 We Met endpoints. */
export interface WeMetConnectionRead {
  id: UUID;
  user_id: UUID;
  connected_user_id: UUID;
  event_id: UUID | null;
  space_id: UUID | null;
  status: WeMetConnectionStatus;
  is_initiator: boolean;
  created_at: ISODateTimeString;
  confirmed_at: ISODateTimeString | null;
  /**
   * The counterpart (the OTHER party in the connection), enriched server-side by
   * EVERY v2 endpoint — create, confirm, decline, and list all return it (see
   * backend `_enrich_connection`). `null` ONLY when that account no longer
   * exists; never null merely because a mutation "forgot" to enrich. That
   * ambiguity — which let the create path silently render blank cards (#919 D7)
   * — no longer exists, so clients must NOT re-introduce a "fall back to a
   * cached row" merge.
   */
  user: UserDetail | null;
  /**
   * Venue name where the users met, enriched server-side from `event_id` /
   * `space_id` by every v2 endpoint. Always present in the payload; `null` when
   * the venue isn't viewable to the viewer, or the connection has no venue of
   * that kind. Exactly one is a name on the happy path (matching whichever id is
   * present); both are null when the present id's venue is withheld.
   *
   * Required, not optional: a *missing* field would signal "unenriched", a state
   * that no longer exists as of backend #537.
   */
  event_name: string | null;
  space_name: string | null;
}

/** The enriched venue name for a connection, or `null` when unavailable. */
export function weMetPlaceName(connection: WeMetConnectionRead): string | null {
  // Both fields are `string | null` (never `undefined`), so this is already
  // `string | null` — no trailing `?? null` normalization needed.
  return connection.event_name ?? connection.space_name;
}

// ── Derived UI state ────────────────────────────────────────────────────────

/**
 * Simplified connection state for UI rendering on the other-user profile.
 * Derived from the raw `WeMetConnectionRead` row.
 */
export type WeMetUIConnectionState = 'none' | 'pending_outgoing' | 'pending_incoming' | 'confirmed';

/**
 * Pure mapping from `(status, is_initiator)` to the UI state. Single source of
 * truth shared by `useWeMetConnectionWith` (other-user profile action row) and
 * `WeMetCard` (own-profile connections list) so the two surfaces can't drift.
 * `deleted` / unknown collapse to `none`.
 */
export function deriveWeMetUIState(
  status: WeMetConnectionStatus,
  isInitiator: boolean,
): WeMetUIConnectionState {
  if (status === WeMetConnectionStatus.CONFIRMED) return 'confirmed';
  if (status === WeMetConnectionStatus.NOT_CONFIRMED) {
    return isInitiator ? 'pending_outgoing' : 'pending_incoming';
  }
  return 'none';
}

// ── Confirm-drawer copy ───────────────────────────────────────────────────────

/** The three destructive We-Met actions that route through a confirmation drawer. */
export type WeMetConfirmAction = 'decline_incoming' | 'cancel_outgoing' | 'remove_confirmed';

export interface WeMetConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
}

/**
 * Copy for the destructive We-Met confirmation drawer, keyed by action. Single
 * source of truth for the four surfaces that render it — the own-profile card,
 * the ⋮ menu, the other-user profile, and the notification — which had drifted
 * ("This cancels" vs "This will cancel"; "always" vs "still connect later"; and
 * the notification dropped the name). `name` is the other person's first name;
 * omit it where the surface has none (e.g. a removed account) and the copy falls
 * back to "they" / "this request".
 */
export function weMetConfirmCopy(action: WeMetConfirmAction, name?: string): WeMetConfirmCopy {
  switch (action) {
    case 'decline_incoming':
      return {
        title: name ? `Decline ${name}'s request?` : 'Decline this request?',
        description: `${name ?? 'They'} won't be notified. You can always connect later.`,
        confirmLabel: 'Decline request',
      };
    case 'cancel_outgoing':
      return {
        title: 'Cancel this request?',
        description: "This cancels your connection request. They won't be able to confirm it.",
        confirmLabel: 'Cancel request',
      };
    case 'remove_confirmed':
      return {
        title: 'Remove this connection?',
        description:
          'This removes the connection from your profile. You can request it again anytime.',
        confirmLabel: 'Remove connection',
      };
  }
}

// ── Notification action derivation ────────────────────────────────────────────

/**
 * What an inline `we_met_request` notification should render right now, derived
 * from the live connections list (the single source of truth) rather than a
 * per-notification flag. Acting from any surface mutates that list, so the
 * notification, the profile card, and the other-user screen stay in sync.
 *
 *  - `unknown`   — list not loaded yet; render nothing (don't flash buttons or a
 *                  premature "closed" state).
 *  - `pending`   — a request the viewer received and hasn't answered → show actions.
 *  - `confirmed` — already confirmed → show a "Connected" state.
 *  - `gone`      — declined / removed (no matching row) → render nothing.
 */
export type WeMetNotificationAction =
  | { kind: 'unknown' }
  | { kind: 'pending'; connectionId: UUID }
  | { kind: 'confirmed' }
  | { kind: 'gone' };

/**
 * Resolve a `we_met_request` notification's actionable state. Matches the
 * connection by the backend-provided `connectionId` when present, else falls
 * back to the actor's id (the request initiator is the notification actor; the
 * viewer's own row points back at them with `is_initiator === false`).
 */
export function resolveWeMetNotificationAction(
  connectionId: UUID | null,
  actorUserId: UUID | null,
  connections: WeMetConnectionRead[] | undefined,
): WeMetNotificationAction {
  if (!connections) return { kind: 'unknown' };

  const match = connections.find(c =>
    connectionId
      ? c.id === connectionId
      : actorUserId != null && c.connected_user_id === actorUserId && !c.is_initiator,
  );

  if (!match) return { kind: 'gone' };

  const uiState = deriveWeMetUIState(match.status, match.is_initiator);
  if (uiState === 'confirmed') return { kind: 'confirmed' };
  if (uiState === 'pending_incoming') return { kind: 'pending', connectionId: match.id };
  return { kind: 'gone' };
}
